"use client";

import React, { useState } from "react";
import Image from "next/image";
import TrackedLink from "@components/TrackedLink";
import ScheduleCall from "@components/ScheduleCall";
import { track } from "@lib/track";
import { feeLabel } from "@lib/fees";

const MONTH1 = feeLabel("farleighMethod");
const ADULT  = feeLabel("consultation");
const CHILD  = feeLabel("childConsultation");
const BODHI  = feeLabel("bodhiClub");

const PAIN_ITEMS = [
  {
    label: "Too much at once",
    body: "You were given ten things to change at once. Within two weeks most had been abandoned, because there was no way to tell which of them were working and which weren't.",
  },
  {
    label: "No clear signal",
    body: "Something shifted, or nothing did, and you're not sure why. With no way to track your response there's nothing to read but noise, and the feeling that you should be working this out on your own.",
  },
  {
    label: "Difficult to adapt",
    body: "Generic plans don't account for how your own body responds. When you don't respond as expected, there's no mechanism to adjust. You're told to persist with something that isn't working.",
  },
  {
    label: "Too much guesswork",
    body: "Every new supplement, elimination, or protocol is a coin toss. Without a structured tracking system, you can't know what's helping, what's hurting, and what's simply irrelevant.",
  },
];

const METHOD_STEPS = [
  {
    label: "Focus",
    heading: "One area. The right one first.",
    body: "Using your history, your symptoms and, where useful, objective test data, we identify the single area most likely to create meaningful progress first. One clear starting point, chosen for your situation, rather than a broad protocol.",
  },
  {
    label: "Measure",
    heading: "Weekly tracking. Every week.",
    body: "A personalised check-in questionnaire, sent and reviewed every week. Tracks subjective signals (energy, sleep, primary symptom, mood) plus focus-specific metrics. Niki reviews each response and uses it to inform the next decision.",
  },
  {
    label: "Adjust",
    heading: "Based on data, not assumption.",
    body: "Based on what the weekly data shows, the protocol changes. Sometimes small refinements. Sometimes a new direction entirely. Nothing stays fixed when the evidence suggests otherwise.",
  },
  {
    label: "Repeat",
    heading: "Until something demonstrably works.",
    body: "We continue the cycle until a personal protocol emerges that demonstrably works, evidenced in your own tracked data. That's the difference between a plan and a method.",
  },
];

const MEASUREMENT_LAYERS = [
  {
    layer: "Layer 1",
    title: "Functional Testing",
    subtitle: "Objective biology",
    body: "Blood panels, gut microbiome screening, hair tissue mineral analysis, hormones, and others when clinically appropriate, all run through partner labs. Month 1 of the Farleigh Method includes one baseline test of your choice. Niki will advise which is most useful for your situation.",
    note: "Tests beyond the included baseline are charged at lab cost, with no markup added.",
  },
  {
    layer: "Layer 2",
    title: "Weekly Check-In Questionnaire",
    subtitle: "Personalised tracking",
    body: "Sent every week, reviewed every week. Personalised to your current focus area and situation. Tracks the metrics that matter to you. Niki, or a Farleigh Method practitioner, reviews each response and uses it to inform the next adjustment.",
    note: "This is the data the whole method runs on.",
  },
  {
    layer: "Layer 3",
    title: "Re-Testing at Month 3",
    subtitle: "Measuring what changed",
    body: "Where a baseline test was run in month 1, a follow-up at month 3 shows what has changed in your own data, rather than in how you feel you ought to be doing. Where retesting is clinically appropriate sooner or later, we'll discuss that.",
    note: "Re-tests are charged at lab cost. Not included in the ongoing monthly fee.",
  },
];

const TIMELINE_ITEMS = [
  {
    period: "Week 1",
    title: "Baseline established",
    body: "Initial consultation. Baseline test ordered if appropriate. First focus area identified. Weekly tracking begins. You know exactly where you are before anything changes.",
  },
  {
    period: "Weeks 2–4",
    title: "First signals emerge",
    body: "Some clients notice changes early. Others, particularly those with complex or long-standing issues, need longer. Either way the tracking is running, so we're working from data rather than impressions.",
  },
  {
    period: "Month 2",
    title: "A pattern starts to show",
    body: "The protocol has likely been adjusted at least once based on your tracked response. A picture of what's working, and what isn't, is beginning to form in your data.",
  },
  {
    period: "Month 3",
    title: "Re-test where appropriate",
    body: "Where a baseline was run in month 1, we compare. You can see on paper, in your own data, what has shifted. This is the measurement delivering its promise.",
  },
  {
    period: "Beyond",
    title: "Continued refinement",
    body: "Ongoing structured support at £200/month until you have a personal protocol that demonstrably works. Timelines vary; complex conditions take longer. We don't rush the evidence.",
  },
];

const NOT_THIS = [
  {
    heading: "We don't promise outcomes we can't control.",
    body: "Your body's response is yours; we can't own it and won't pretend to. What we do promise is to measure it properly and respond intelligently to what the data shows.",
  },
  {
    heading: "We're not a replacement for medical care.",
    body: "For serious or complex conditions, we work alongside conventional treatment, never instead of it. The Farleigh Method is complementary care, always.",
  },
  {
    heading: "We don't sell protocols.",
    body: "The Farleigh Method is the opposite of a one-size-fits-all plan. There's no standard protocol, only yours, built from your data and adjusted as your body responds.",
  },
];

const TESTIMONIALS = [
  {
    quote: "I've never felt so well, so grounded, and so in tune with myself.",
    body: "Since completing my breast cancer treatment last year, Nichola helped me detox and reclaim my health. I feel emotionally and spiritually stronger than ever.",
    name: "Lucie W.",
    tag: "Nutrition Through Cancer Treatment",
  },
  {
    quote: "I always feel amazing afterwards — my inflammation and Psoriasis improve every time.",
    body: "She has made something that seems quite overwhelming and complicated very accessible and simple. I come back to Niki again and again.",
    name: "Johnathan",
    tag: "Repeat Client",
  },
  {
    quote: "Every bit of advice from Niki has made me feel healthier.",
    body: "The gut cleanse was unreal — I felt so much better afterwards, and losing a few kilos was a great side note.",
    name: "Helen",
    tag: "Ongoing Guidance",
  },
];

const FAQS = [
  {
    q: "Why is a functional test included in month 1?",
    a: "Because measurement is the heart of the method. Starting with objective data, a baseline of what's happening in your biology, means we know where you are before we change anything. It gives us a reference point to return to, and something concrete to compare at month 3.",
  },
  {
    q: "What if I've already had tests done recently?",
    a: "Bring them. If they're recent enough and comprehensive enough, we'll use those. In that case, the value of the included test can be applied to something more useful for your specific situation. We won't run a test for the sake of running one.",
  },
  {
    q: "Do I have to do the functional testing?",
    a: "No. If you'd prefer to work from history and symptoms alone, that's a valid starting point. The included test isn't compulsory. Some clients choose to hold it until the tracking picture suggests where objective data would be most useful.",
  },
  {
    q: "What if I have a complex or serious condition?",
    a: "Complex cases, including nutrition through cancer treatment, Parkinson's, serious hormonal disorders and autoimmune conditions, take longer and may benefit from more comprehensive testing than the standard inclusion. We'll discuss this on the discovery call. A higher tier with additional included testing is available where clinically appropriate. We'll never rush a complex case to fit a standard timeline.",
  },
  {
    q: "What kinds of health issues does the method work for?",
    a: "The Farleigh Method works across a wide range of presentations: chronic fatigue, poor sleep, gut symptoms, hormonal imbalance, brain fog, weight difficulty, and persistent symptoms that haven't responded to previous approaches. It also supports complementary care for more serious conditions, always alongside conventional treatment and never instead of it.",
  },
  {
    q: "How is this different from a standard nutrition consultation?",
    a: "A standard consultation gives you a protocol and sends you away. The Farleigh Method is a structured ongoing process with a measurement system at its core: weekly tracking, regular review, and functional testing as objective anchors. You're never handed a plan and left to guess whether it's working.",
  },
  {
    q: "How long does the programme last?",
    a: `Month 1 is ${MONTH1} and covers the initial consultation, four weeks of structured support, and one baseline functional test. After that, we continue at £200/month. Timelines vary significantly depending on complexity. Simple presentations may resolve in 2–3 months. Complex or long-standing conditions often take longer. We track progress rather than predict it.`,
  },
  {
    q: "How much time does it take each week?",
    a: "The weekly check-in questionnaire takes around 5–10 minutes. The protocol itself is built to fit into your real life.",
  },
  {
    q: "How do you protect my health data?",
    a: "[Niki / team to confirm — this will cover weekly questionnaire data, functional test results, and any health information stored during and after the programme.]",
  },
];

export default function MethodClient() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [showConsultation, setShowConsultation] = useState(false);

  const revealAndScroll = (id: string, reveal: () => void) => {
    reveal();
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const scrollToScheduler: React.MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();
    revealAndScroll("book-discovery", () => setShowDiscovery(true));
  };

  return (
    <div className="relative bg-[var(--color-brand)] text-[var(--text-on-dark)]">

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <header className="mx-auto max-w-5xl px-5 md:px-10 py-12 md:py-20 text-center">
        <p className="text-xs md:text-sm uppercase tracking-widest opacity-70 mb-4 font-semibold">
          The Farleigh Method — Signature Programme
        </p>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight text-balance">
          Measurement, Not Protocols.
        </h1>

        <p className="mt-5 text-sm md:text-lg opacity-90 max-w-3xl mx-auto leading-relaxed text-pretty">
          A 1:1 nutrition programme built around weekly personalised tracking and objective
          functional testing. Adaptive and personal, because your body's response is the only
          data that matters.
        </p>

        <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-2 text-sm">
          Month 1 includes one baseline functional test of your choice, at no extra cost.
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center items-center">
          <TrackedLink
            href="#schedule-call"
            onClick={scrollToScheduler}
            variant="solid"
            size="lg"
            color="base"
            event="cta_click"
            label="Farleigh Method | Book Hero Discovery"
            component="MethodPage"
          >
            Book a free discovery call
          </TrackedLink>
          <button
            onClick={() => {
              revealAndScroll("book-consultation", () => setShowConsultation(true));
              try { track("cta_click", { label: "Farleigh Method | Begin Month 1 Hero", component: "MethodPage" }, { event: "cta_click", label: "Farleigh Method | Begin Month 1 Hero", component: "MethodPage" }); } catch {}
            }}
            className="inline-flex items-center justify-center rounded-full border border-white/40 text-white font-semibold px-7 py-3 text-base hover:bg-white/10 transition"
          >
            Begin Month 1 →
          </button>
        </div>

        <p className="mt-3 text-xs md:text-sm opacity-70">
          Discovery call is free and takes 15 minutes.
        </p>
        <p className="mt-2 text-xs opacity-50">
          Named after Niki's daughter, a reminder that this work is personal.
        </p>
      </header>

      {/* ── BODY ─────────────────────────────────────────────────────────────── */}
      <main className="mx-auto max-w-5xl -mt-6 rounded-t-3xl bg-[var(--bg-surface)] px-5 md:px-10 pb-20 shadow-2xl text-[var(--text-on-light)]">

        {/* ── THE PROBLEM ───────────────────────────────────────────────────── */}
        <section className="pt-10 md:pt-16 max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] text-center">
            Why Plans and Protocols Often Fall Short
          </h2>
          <p className="mt-3 text-sm text-[var(--text-on-light-muted)] text-center max-w-2xl mx-auto">
            Most programmes fail not because the advice was wrong, but because there was no way to
            tell whether it was working.
          </p>
          <div className="mt-8 space-y-4">
            {PAIN_ITEMS.map((item, i) => (
              <div key={i} className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-4 shadow-sm flex gap-4 items-start">
                <span className="mt-0.5 text-[var(--text-brand)] font-bold shrink-0 text-sm">—</span>
                <div>
                  <p className="font-semibold text-sm text-[var(--text-brand)]">{item.label}</p>
                  <p className="text-sm text-[var(--text-on-light-muted)] mt-0.5 leading-relaxed">{item.body}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-5 shadow-sm text-center">
            <p className="text-base md:text-lg font-semibold text-[var(--text-brand)]">
              The problem isn't willpower. It's that you've never had a way to see what's working.
            </p>
          </div>
        </section>

        {/* ── THE METHOD ────────────────────────────────────────────────────── */}
        <section className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] text-center">
            The Farleigh Method
          </h2>
          <p className="mt-3 text-sm md:text-base text-[var(--text-on-light-muted)] text-center leading-relaxed">
            Most health programmes are built around what's worked on average, for many people.
            The Farleigh Method is built around what's working for you right now, based on your own response.
          </p>

          <div className="mt-6 flex justify-center">
            <div className="inline-flex rounded-full border border-[var(--border-accent)] bg-[var(--bg-card)] px-5 py-3 shadow-sm">
              <p className="font-semibold text-[var(--text-brand)] text-sm tracking-wide">
                Focus → Measure → Adjust → Repeat
              </p>
            </div>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {METHOD_STEPS.map(({ label, heading, body }) => (
              <div key={label} className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-5 shadow-sm">
                <p className="text-xs uppercase tracking-widest font-semibold text-[var(--text-on-light-muted)] mb-1">{label}</p>
                <h3 className="font-semibold text-[var(--text-brand)] mb-2">{heading}</h3>
                <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── HOW WE MEASURE ────────────────────────────────────────────────── */}
        <section className="mt-16">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)]">
              How We Measure
            </h2>
            <p className="mt-3 text-sm md:text-base text-[var(--text-on-light-muted)] leading-relaxed">
              Measurement is not a feature of the Farleigh Method. It is the method. Three layers,
              working together, give us a complete picture of what's changing and why.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {MEASUREMENT_LAYERS.map((layer, i) => (
              <div key={i} className="rounded-2xl border-2 border-[var(--border-accent)] bg-[var(--bg-card)] p-6 shadow-sm flex flex-col gap-3">
                <div className="w-10 h-10 rounded-full bg-[var(--color-brand)]/10 flex items-center justify-center text-[var(--text-brand)]">
                  {i === 0 && (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 3h6M9 3v8.5L5.5 18a1 1 0 00.9 1.5h11.2a1 1 0 00.9-1.5L15 11.5V3" />
                      <circle cx="10.5" cy="15.5" r="0.75" fill="currentColor" stroke="none" />
                      <circle cx="13" cy="13.5" r="0.75" fill="currentColor" stroke="none" />
                    </svg>
                  )}
                  {i === 1 && (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                      <path d="M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      <path d="M9 12l2 2 4-4" />
                    </svg>
                  )}
                  {i === 2 && (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 17l5-5 4 4 9-9" />
                      <path d="M17 7h4v4" />
                    </svg>
                  )}
                </div>
                <p className="text-xs uppercase tracking-widest text-[var(--text-on-light-muted)] font-semibold">{layer.layer}</p>
                <div>
                  <h3 className="font-bold text-[var(--text-brand)]">{layer.title}</h3>
                  <p className="text-xs text-[var(--text-on-light-muted)] font-medium">{layer.subtitle}</p>
                </div>
                <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed flex-1">{layer.body}</p>
                <p className="text-xs text-[var(--text-on-light-muted)] italic border-t border-[var(--border-accent)] pt-3">{layer.note}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── PRACTITIONER ──────────────────────────────────────────────────── */}
        <section className="mt-16 max-w-3xl mx-auto rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-6 md:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <Image
              src="/images/Niki_Charnock.webp"
              alt="Niki Charnock — Naturopathic Nutritionist"
              width={80}
              height={80}
              className="rounded-full object-cover shrink-0"
            />
            <div>
              <p className="text-xs uppercase tracking-widest text-[var(--text-on-light-muted)] font-semibold mb-1">
                Why I Built This
              </p>
              <h3 className="text-lg font-bold text-[var(--text-brand)]">
                Niki Charnock
              </h3>
              <p className="text-xs text-[var(--text-on-light-muted)] mb-3">
                Naturopathic Nutritionist · BSc Sports Rehabilitation (Salford University) ·
                Trained at the College of Naturopathic Medicine, London ·
                Member, Association of Naturopathic Practitioners (ANP)
              </p>
              <p className="text-sm md:text-base text-[var(--text-on-light-muted)] leading-relaxed">
                I spent years working in elite sports rehabilitation, where I watched athletes recover, or fail to,
                and realised that what they ate, how they slept, and how they managed stress shaped outcomes more
                than any single treatment. That pulled me into naturopathic nutrition and functional health, and
                eventually to CF Organic Wellness. The Farleigh Method came out of a pattern I kept seeing in clinic:
                people who had already tried a lot of things, been given more things to try, and still weren't
                getting anywhere. The problem wasn't effort. It was never having a clear way to tell what was
                working.
              </p>
              <TrackedLink
                href="/about/founder"
                className="mt-3 inline-block text-xs underline text-[var(--text-brand)]"
                label="Farleigh Method | Niki Bio Link"
                component="MethodPage"
              >
                Read Niki's full story →
              </TrackedLink>
            </div>
          </div>
        </section>

        {/* ── TIMELINE ──────────────────────────────────────────────────────── */}
        <section className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] text-center mb-3">
            What Progress Looks Like
          </h2>
          <p className="text-sm text-[var(--text-on-light-muted)] text-center mb-8 max-w-2xl mx-auto">
            Timelines vary, especially for complex or long-standing conditions. This is a typical
            picture rather than a guarantee.
          </p>
          <div className="space-y-0">
            {TIMELINE_ITEMS.map((item, i) => (
              <div key={i} className="relative flex gap-5 pb-6">
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 rounded-full bg-[var(--color-brand)] text-[var(--text-on-dark)] text-xs font-bold flex items-center justify-center shrink-0 z-10">
                    {i + 1}
                  </div>
                  {i < TIMELINE_ITEMS.length - 1 && (
                    <div className="w-0.5 flex-1 bg-[var(--border-accent)] mt-1" />
                  )}
                </div>
                <div className="pb-2">
                  <p className="text-xs uppercase tracking-widest font-semibold text-[var(--text-on-light-muted)] mb-0.5">{item.period}</p>
                  <h3 className="font-semibold text-[var(--text-brand)] mb-1">{item.title}</h3>
                  <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── TESTIMONIALS ──────────────────────────────────────────────────── */}
        <section className="mt-16">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] text-center mb-2">
            What Clients Say
          </h2>
          <p className="text-sm text-[var(--text-on-light-muted)] text-center mb-8 max-w-2xl mx-auto">
            From clients working with Niki across her programmes.
          </p>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-5 shadow-sm flex flex-col gap-3">
                <p className="text-sm font-semibold text-[var(--text-brand)] italic leading-snug">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed flex-1">
                  {t.body}
                </p>
                <div>
                  <p className="text-sm font-semibold text-[var(--text-brand)]">{t.name}</p>
                  <p className="text-xs text-[var(--text-on-light-muted)]">{t.tag}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── WHAT THIS IS NOT ──────────────────────────────────────────────── */}
        <section className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] text-center mb-6">
            What This Is Not
          </h2>
          <div className="space-y-3">
            {NOT_THIS.map((item, i) => (
              <div key={i} className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-5 shadow-sm">
                <p className="font-semibold text-[var(--text-brand)] mb-1">{item.heading}</p>
                <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── PRICING ───────────────────────────────────────────────────────── */}
        <section className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] text-center mb-3">
            Pricing
          </h2>
          <p className="text-sm text-[var(--text-on-light-muted)] text-center mb-8 max-w-2xl mx-auto">
            Start with a free discovery call. You decide the rest in your own time.
          </p>

          <div className="space-y-3">
            {/* Discovery call */}
            <div className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-5 shadow-sm flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <p className="font-semibold text-[var(--text-brand)]">Discovery Call</p>
                <p className="text-xs text-[var(--text-on-light-muted)] mb-1">15 minutes · Free</p>
                <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed">
                  A conversation to understand your situation and work out whether the Farleigh Method is the right fit.
                </p>
              </div>
              <div className="shrink-0">
                <button
                  onClick={() => {
                    revealAndScroll("book-discovery", () => setShowDiscovery(true));
                    try { track("cta_click", { label: "Farleigh Method | Book Free Call Pricing", component: "MethodPage" }, { event: "cta_click", label: "Farleigh Method | Book Free Call Pricing", component: "MethodPage" }); } catch {}
                  }}
                  className="inline-flex items-center justify-center rounded-full border border-[var(--text-brand)] text-[var(--text-brand)] font-semibold px-5 py-2 text-sm hover:bg-[var(--text-brand)]/5 transition whitespace-nowrap"
                >
                  Book a free discovery call
                </button>
              </div>
            </div>

            {/* One-off consultation */}
            <div className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-5 shadow-sm flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1">
                <p className="font-semibold text-[var(--text-brand)]">One-Off Consultation</p>
                <p className="text-xs text-[var(--text-on-light-muted)] mb-1">60 minutes · {ADULT} (adults) · {CHILD} (children)</p>
                <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed">
                  A single hour to assess your history, identify priorities, and build a starting protocol. No programme commitment required.
                </p>
              </div>
              <div className="shrink-0">
                <button
                  onClick={() => {
                    revealAndScroll("book-consultation", () => setShowConsultation(true));
                    try { track("cta_click", { label: "Farleigh Method | Book Consultation Pricing", component: "MethodPage" }, { event: "cta_click", label: "Farleigh Method | Book Consultation Pricing", component: "MethodPage" }); } catch {}
                  }}
                  className="inline-flex items-center justify-center rounded-full border border-[var(--text-brand)] text-[var(--text-brand)] font-semibold px-5 py-2 text-sm hover:bg-[var(--text-brand)]/5 transition whitespace-nowrap"
                >
                  Book consultation
                </button>
              </div>
            </div>

            {/* Farleigh Method Month 1 — featured */}
            <div className="rounded-2xl border-2 border-[var(--text-brand)] bg-[var(--bg-card)] p-6 shadow-md">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <p className="font-bold text-[var(--text-brand)]">Farleigh Method — Month 1</p>
                    <span className="text-xs bg-[var(--color-brand)] text-[var(--text-on-dark)] rounded-full px-2 py-0.5 font-semibold">Recommended start</span>
                  </div>
                  <p className="text-xs text-[var(--text-on-light-muted)] mb-3">4 weeks + baseline test · {MONTH1}</p>
                  <ul className="space-y-1.5 mb-3">
                    {[
                      "Initial 60-minute consultation",
                      "Four weeks of personalised weekly tracking and check-ins",
                      "One baseline functional test of your choice (bloods, gut microbiome, or hair tissue mineral analysis)",
                      "Niki reviews your data every week and adjusts the protocol accordingly",
                    ].map((item, i) => (
                      <li key={i} className="flex gap-2 items-start text-sm text-[var(--text-on-light-muted)]">
                        <span className="text-green-600 font-bold shrink-0 mt-0.5">✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-[var(--text-on-light-muted)] italic">
                    Functional tests typically cost £80–150 if ordered separately.
                  </p>
                </div>
                <div className="shrink-0">
                  <button
                    onClick={() => {
                      revealAndScroll("book-consultation", () => setShowConsultation(true));
                      try { track("cta_click", { label: "Farleigh Method | Begin Month 1 Pricing", component: "MethodPage" }, { event: "cta_click", label: "Farleigh Method | Begin Month 1 Pricing", component: "MethodPage" }); } catch {}
                    }}
                    className="inline-flex items-center justify-center rounded-full bg-[var(--color-brand)] text-[var(--text-on-dark)] font-semibold px-6 py-2.5 text-sm hover:opacity-90 transition whitespace-nowrap"
                  >
                    Begin Month 1
                  </button>
                </div>
              </div>
            </div>

            {/* Ongoing */}
            <div className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] p-5 shadow-sm">
              <p className="font-semibold text-[var(--text-brand)]">Ongoing — Month 2+</p>
              <p className="text-xs text-[var(--text-on-light-muted)] mb-1">£200/month · cancel any time</p>
              <p className="text-sm text-[var(--text-on-light-muted)] leading-relaxed">
                Continued weekly tracking, support, and protocol adjustment for as long as it's useful.
                When your symptoms are managed and you want lighter maintenance, step across to the{" "}
                <TrackedLink
                  href="/workwithme/align"
                  className="underline text-[var(--text-brand)]"
                  label="Farleigh Method | Pricing Bodhi Club Link"
                  component="MethodPage"
                >
                  Bodhi Club
                </TrackedLink>{" "}
                from {BODHI}/month.
              </p>
            </div>
          </div>
        </section>

        {/* ── FAQ ───────────────────────────────────────────────────────────── */}
        <section className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-[var(--text-brand)] text-center mb-8">
            Common Questions
          </h2>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="rounded-2xl border border-[var(--border-accent)] bg-[var(--bg-card)] shadow-sm overflow-hidden">
                <button
                  className="w-full text-left px-5 py-4 flex justify-between items-center gap-4 min-h-[44px]"
                  onClick={() => {
                    const newOpen = openFaq === i ? null : i;
                    setOpenFaq(newOpen);
                    try {
                      track(
                        "accordion_toggle",
                        { label: `FAQ: ${faq.q}`, component: "MethodPage" },
                        { event: "accordion_toggle", label: `FAQ: ${faq.q}`, component: "MethodPage", meta: { state: newOpen === i ? "Opened" : "Closed" } }
                      );
                    } catch {}
                  }}
                >
                  <span className="font-semibold text-sm md:text-base text-[var(--text-brand)]">{faq.q}</span>
                  <span className="text-[var(--text-on-light-muted)] shrink-0 text-lg leading-none">
                    {openFaq === i ? "−" : "+"}
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-[var(--text-on-light-muted)] leading-relaxed border-t border-[var(--border-accent)]">
                    <p className="pt-3">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ── CLOSE ─────────────────────────────────────────────────────────── */}
        <section className="mt-16 max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold text-[var(--text-brand)] leading-tight">
            Start with measurement.<br />See what changes.
          </h2>
          <p className="mt-4 text-sm md:text-base text-[var(--text-on-light-muted)] leading-relaxed">
            If you've read this far, you have a sense of whether this is the right fit.
            The discovery call is 15 minutes and free, a conversation to see whether the Farleigh
            Method is the right next step for your situation.
          </p>
          <div className="mt-8">
            <button
              onClick={() => {
                revealAndScroll("book-discovery", () => setShowDiscovery(true));
                try { track("cta_click", { label: "Farleigh Method | Book Discovery Close", component: "MethodPage" }, { event: "cta_click", label: "Farleigh Method | Book Discovery Close", component: "MethodPage" }); } catch {}
              }}
              className="inline-flex items-center justify-center rounded-full bg-[var(--color-brand)] text-[var(--text-on-dark)] font-semibold px-7 py-3 text-base hover:opacity-90 transition"
            >
              Book a free discovery call
            </button>
          </div>
          <p className="mt-5 text-sm text-[var(--text-on-light-muted)]">
            Ready to begin?{" "}
            <button
              onClick={() => {
                revealAndScroll("book-consultation", () => setShowConsultation(true));
                try { track("cta_click", { label: "Farleigh Method | Begin Month 1 Close", component: "MethodPage" }, { event: "cta_click", label: "Farleigh Method | Begin Month 1 Close", component: "MethodPage" }); } catch {}
              }}
              className="underline text-[var(--text-brand)] cursor-pointer"
            >
              Book Month 1 directly →
            </button>
          </p>
        </section>

        {/* ── PAID CONSULTATION WIDGET ──────────────────────────────────────── */}
        <section id="book-consultation" className="scroll-mt-24">
          {showConsultation && (
            <div className="mt-10">
              <ScheduleCall
                duration={60}
                meetingType="farleigh-method"
                title="Farleigh Method — Initial Consultation"
                subtitle="Your 60-minute deep-dive. We'll map your history, identify the first priority, and build your starting protocol."
                ctaText="Confirm & Book"
                componentName="MethodPage"
              />
            </div>
          )}
        </section>

        {/* ── DISCOVERY CALL WIDGET ─────────────────────────────────────────── */}
        <section id="book-discovery" className="scroll-mt-24">
          {showDiscovery && (
            <div className="mt-10">
              <ScheduleCall
                duration={15}
                /* Must stay "farleigh-discovery", not "farleigh-method": queryClosureProxy
                   (app/api/growth-db/route.ts) builds its discovery cohort with
                   meeting_type ILIKE '%discovery%'. Tagged as farleigh-method, this free
                   fit-check call landed in neither side of the close rate, so the
                   free-call -> signature-programme progression was invisible. The paid
                   widget above is the one that stays "farleigh-method". */
                meetingType="farleigh-discovery"
                title="Free Discovery Call"
                subtitle="A short call to understand your situation, answer your questions, and see whether the Farleigh Method is the right fit for you."
                ctaText="Book Your Call"
                componentName="MethodPage"
                isFree
              />
            </div>
          )}
        </section>

        <div className="mt-10 text-center max-w-2xl mx-auto">
          <p className="text-xs text-[var(--text-on-light-muted)]">
            Not ready for 1-to-1 support yet? Take a look at the{" "}
            <TrackedLink
              href="/workwithme/align"
              className="underline text-[var(--text-brand)]"
              event="cta_click"
              label="Farleigh Method | View Bodhi Club"
              component="MethodPage"
            >
              Bodhi Club
            </TrackedLink>
            , guided group wellness from {BODHI}/month.
          </p>
        </div>
      </main>

      {/* ── STICKY MOBILE CTA ─────────────────────────────────────────────────── */}
      <div className="fixed inset-x-0 bottom-0 z-40 md:hidden border-t border-[var(--border-on-light)] bg-[var(--bg-surface)]/95 backdrop-blur supports-[backdrop-filter]:bg-[var(--bg-surface)]/70">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
          <span className="text-sm font-medium text-[var(--text-on-light)]">The Farleigh Method</span>
          <button
            onClick={() => {
              revealAndScroll("book-discovery", () => setShowDiscovery(true));
              try {
                track(
                  "cta_click",
                  { label: "Farleigh Method | Book Sticky", component: "MethodPage" },
                  { event: "cta_click", label: "Farleigh Method | Book Sticky", component: "MethodPage" }
                );
              } catch {}
            }}
            className="inline-flex items-center gap-1 rounded-full bg-[var(--color-brand)] text-[var(--text-on-dark)] font-semibold text-sm px-5 py-2.5 hover:opacity-90 transition"
          >
            Book a call →
          </button>
        </div>
      </div>
    </div>
  );
}
